import { useState, useEffect, useMemo } from 'react';
import { Container, Header, Form, Dropdown, Input, TextArea, Button, Message, Divider, Loader, Segment } from 'semantic-ui-react';
import { useQuery, useLazyQuery, useMutation, gql } from '@apollo/client';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PATHS } from '../../../paths';
import { useIsAdmin } from '../../../hooks/isAdmin';
import { useHasMultipleOrganisations, useRunsClasses } from '../../../hooks/organisations';
import { useBlocks } from '../../../hooks/useBlocks';
import { GET_ME } from '../../../queries/getMe';
import { settingsSections } from './gates';

const GET_BLOCK_TAGS = gql`
  query BlockTags {
    blockTags {
      id
      name
      code
    }
  }
`;

/**
 * What tomorrow morning's attendance digest would send.
 *
 * `useLazyQuery`, not `useQuery`: it walks every session in a three-day window for the whole
 * organisation, and nobody opening Settings to change a block tag should pay for that. It runs
 * when the button is pressed and not before.
 */
const DIGEST_PREVIEW = gql`
  query AttendanceDigestPreview {
    attendanceDigestPreview {
      period
      sendsAt
      timezone
      lookbackDays
      unmanagedClasses
      asOf
      recipients {
        managerUserId
        managerName
        email
        classes { courseId courseName sku day startedAt expected teacherNames }
      }
    }
  }
`;

const GET_TIMEZONE = gql`
  query OrganisationTimezone {
    me { organisation { id name timezone } }
  }
`;

const SET_TIMEZONE = gql`
  mutation SetOrganisationTimezone($timezone: String) {
    setOrganisationTimezone(timezone: $timezone) { timezone }
  }
`;

const SET_ORGANISATION_BLOCKS = gql`
  mutation SetOrganisationBlocks($currentBlockTagId: Int, $nextBlockTagId: Int) {
    setOrganisationBlocks(currentBlockTagId: $currentBlockTagId, nextBlockTagId: $nextBlockTagId) {
      currentBlockTagId
      nextBlockTagId
    }
  }
`;

const SEND_ANNOUNCEMENT = gql`
  mutation SendAnnouncement($kind: String!, $title: String!, $body: String!) {
    sendAnnouncement(kind: $kind, title: $title, body: $body) {
      id
    }
  }
`;

const ANNOUNCEMENT_RECIPIENT_COUNT = gql`
  query AnnouncementRecipientCount($kind: String!) {
    announcementRecipientCount(kind: $kind) {
      subscribers
      recipients
    }
  }
`;

const KIND_OPTIONS = [
  { key: 'APPLICATION', value: 'APPLICATION', text: 'Application Announcement' },
  { key: 'PRODUCT',     value: 'PRODUCT',     text: 'Product Announcement' },
];

const NONE = '__none__';

const STREAMS_GATE = gql`
  query StreamsGate {
    streams { id }
  }
`;

/**
 * The three server-answered gates, in one document.
 *
 * `canConfigureSkuRules` is here rather than in a query of its own because this one already
 * fetched two gates — and because it is the answer the SkuRules page itself uses to decide
 * whether to redirect back here. Asking the same question the destination asks is what stops
 * Settings offering a button that bounces.
 */
const SETTINGS_GATES = gql`
  query SettingsGates {
    canAdministerRoles
    canCreateOrganisation
    canConfigureSkuRules
  }
`;

export const AdminSettings = () => {
  const isAdmin = useIsAdmin();
  const hasMultipleOrganisations = useHasMultipleOrganisations();
  const { currentBlockTagId, nextBlockTagId, loading: blocksLoading } = useBlocks();

  // Skipped for non-admins. The page renders for everybody now, but these two queries back the
  // admin-only sections below — firing them for a teacher or a membership officer would mean a
  // request that can only fail, on every visit.
  const { data: blockTagsData, loading: tagsLoading } = useQuery(GET_BLOCK_TAGS, { skip: !isAdmin });
  const { data: gates } = useQuery(SETTINGS_GATES);
  const { data: streamsGate } = useQuery(STREAMS_GATE);
  const runsClasses = useRunsClasses();
  const [setOrganisationBlocks, { loading: saving, error }] = useMutation(SET_ORGANISATION_BLOCKS);

  // The organisation's own clock. Read by anybody (the scheduler needs it); set by whoever
  // administers the data.
  const { data: tzData } = useQuery(GET_TIMEZONE);
  const [setTimezone, { loading: savingTimezone, error: timezoneError }] = useMutation(SET_TIMEZONE, {
    refetchQueries: [{ query: GET_TIMEZONE }],
  });
  const [timezoneDraft, setTimezoneDraft] = useState<string | null>(null);
  const [loadDigestPreview, { data: digestData, loading: digestLoading, error: digestError }] =
    useLazyQuery(DIGEST_PREVIEW, { fetchPolicy: 'network-only' });
  const preview = digestData?.attendanceDigestPreview;
  const storedTimezone: string | null = tzData?.me?.organisation?.timezone ?? null;
  // Already fetched by GET_TIMEZONE above, which has no `skip` — so naming the organisation on
  // the page costs nothing.
  const organisationName: string | null = tzData?.me?.organisation?.name ?? null;
  const timezoneValue = timezoneDraft ?? storedTimezone ?? '';

  /**
   * Every zone the browser knows, so the list cannot go stale and cannot disagree with the
   * server's own validation (both ask the same ICU database). `supportedValuesOf` is absent on
   * older Safari; the fallback keeps the control usable rather than rendering an empty select.
   */
  const timezoneOptions = useMemo(() => {
    const anyIntl = Intl as any;
    const zones: string[] = typeof anyIntl.supportedValuesOf === 'function'
      ? anyIntl.supportedValuesOf('timeZone')
      : [Intl.DateTimeFormat().resolvedOptions().timeZone].filter(Boolean);

    return zones.map((zone) => ({ key: zone, value: zone, text: zone.replace(/_/g, ' ') }));
  }, []);

  const [currentId, setCurrentId] = useState<number | null>(null);
  const [nextId, setNextId] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  // Push notification announcement
  const [kind, setKind] = useState<'APPLICATION' | 'PRODUCT'>('APPLICATION');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const [sendAnnouncement, { loading: sending, error: sendError }] = useMutation(SEND_ANNOUNCEMENT);

  const { data: countData, loading: countLoading } = useQuery(ANNOUNCEMENT_RECIPIENT_COUNT, {
    variables: { kind },
    skip: !isAdmin,
  });

  useEffect(() => {
    if (!blocksLoading) {
      setCurrentId(currentBlockTagId);
      setNextId(nextBlockTagId);
    }
  }, [blocksLoading, currentBlockTagId, nextBlockTagId]);

  // Deliberately **not** `if (!isAdmin) return null`, which this page used to open with.
  // A supporter organisation's leadership and membership officer are not admins, and they are
  // exactly the people most likely to belong to two organisations and need the switcher below.
  // Every section carries its own gate, so a non-admin simply sees fewer of them.

  const tags = [...((blockTagsData?.blockTags ?? []) as { id: number; name: string; code: string }[])]
    .sort((a, b) => a.id - b.id);

  // One place decides what this organisation shows, and it is unit-tested against all eight
  // production organisations in `gates.test.ts`. The JSX below asks it rather than re-deriving
  // the rule inline, which is how the three class sections drifted out of step in the first
  // place.
  const sections = settingsSections({
    isAdmin,
    canAdministerRoles: gates?.canAdministerRoles === true,
    canCreateOrganisation: gates?.canCreateOrganisation === true,
    canConfigureSkuRules: gates?.canConfigureSkuRules === true,
    runsClasses,
    hasStreams: (streamsGate?.streams?.length ?? 0) > 0,
    blockTagCount: tags.length,
    hasMultipleOrganisations,
  });

  const tagOptions = tags.map((t) => ({
    key: t.id,
    value: t.id,
    text: `${t.name} (${t.code})`,
  }));

  const nextTagOptions = [
    { key: 'none', value: NONE, text: '— none —' },
    ...tagOptions,
  ];

  const handleSave = async () => {
    setSaved(false);
    await setOrganisationBlocks({
      variables: { currentBlockTagId: currentId, nextBlockTagId: nextId },
      refetchQueries: [{ query: GET_ME }],
    });
    setSaved(true);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return;
    await sendAnnouncement({ variables: { kind, title, body } });
    setSent(true);
    setTitle('');
    setBody('');
  };

  const count = countData?.announcementRecipientCount as { subscribers: number; recipients: number } | undefined;

  const recipientSummary = () => {
    if (countLoading) {
      return <span><Loader active inline size="tiny" /> Counting recipients…</span>;
    }
    if (!count || count.subscribers === 0) {
      return <span>No subscribed app users yet for this type.</span>;
    }
    return (
      <span>
        ≈ <strong>{count.recipients}</strong> app user{count.recipients === 1 ? '' : 's'} will receive this
        {' '}— {count.subscribers} subscribed, {count.recipients} with the Joyya app installed.
      </span>
    );
  };

  return (
    <ResponsiveContainer>
      <Container style={{ paddingTop: '2em', maxWidth: '600px' }}>
        {/* Named unconditionally, not just for multi-organisation people. Sections now appear
            and disappear between organisations, and without a name on the page their absence
            reads as "did this break?" rather than "this organisation doesn't do that". The nav
            names the organisation on desktop but the mobile bottom bar does not, which made
            Settings the one admin screen on a phone with no statement of what it was about. */}
        <Header as="h1">
          Settings
          {organisationName && (
            <Header.Subheader style={{ marginTop: '0.3em' }}>
              For <strong>{organisationName}</strong>. Switching organisation changes what is here.
            </Header.Subheader>
          )}
        </Header>

        {/* Shown only to people who belong to more than one organisation. The same entry is in
            the user menu, which is where people look first; this one is here because the
            "you've been added to an organisation" email points at Settings. */}
        {sections.has('SWITCH_ORGANISATION') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Your organisations</Header>
              <p style={{ color: '#767676' }}>
                You belong to more than one. Switch between them here.
              </p>
              <Button as="a" href={PATHS.ORGANISATIONS}>Switch organisation</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* One person holds `CREATE_ORGANISATION` — deliberately not `canAdministerRoles`,
            which every organisation's administrator holds for their own organisation. */}
        {sections.has('SUPPORTING_ORGANISATIONS') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Supporting organisations</Header>
              <p style={{ color: '#767676' }}>
                Bring a church or partner organisation into Joyya and invite their people.
              </p>
              <Button as="a" href={PATHS.ADMIN_SUPPORTER_INVITE}>
                Invite a supporting organisation
              </Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* Only shown to whoever administers the organisation — after Phase 9 that is the
            authority over *who can do what*, separate from managing the data itself. The server
            decides via `canAdministerRoles`; this just avoids a link that leads to a redirect. */}
        {sections.has('ROLES') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Roles and permissions</Header>
              <p style={{ color: '#767676' }}>
                What each role grants, and who holds it.
              </p>
              <Button as="a" href={PATHS.ADMIN_ROLES}>Manage roles</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* Lifted out of the `canAdministerRoles` block above, which it used to share. Two
            separate problems lived in that pairing: an organisation with no classes was offered
            rules about class codes, and the gate was not the one the destination checks — the
            SkuRules page redirects straight back here on `canConfigureSkuRules === false`, so
            the button could bounce. `gates.ts` now asks both questions. */}
        {sections.has('CLASS_CODE_RULES') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Class code rules</Header>
              <p style={{ color: '#767676' }}>
                Which stream, program and class label combinations a class code may use.
              </p>
              <Button as="a" href={PATHS.ADMIN_SKU_RULES}>Manage class codes</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* Admin only, not `canAdministerRoles`: this writes class data in bulk, which is data
            administration rather than authority over who can do what. Also requires classes to
            exist — it sets the manager and absence limit *across many classes chosen by tag*,
            so with none it is a form that acts on nothing. */}
        {sections.has('CLASS_DEFAULTS') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Class defaults</Header>
              <p style={{ color: '#767676' }}>
                Set the manager and the absence limit across many classes at once, chosen by tag.
                The manager is emailed each morning about classes with no attendance recorded.
              </p>
              <Button as="a" href={PATHS.ADMIN_CLASS_DEFAULTS}>Set class defaults</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* The digest goes to class managers at 9am and nobody else ever sees it, which makes
            two ordinary questions unanswerable: is it working, and why did somebody get mailed
            about a class they do not manage. This answers both without waiting for 9am or
            reading the job's log on the box. */}
        {sections.has('ATTENDANCE_DIGEST') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Tomorrow's attendance emails</Header>
              <p style={{ color: '#767676' }}>
                Each class manager is emailed the registers nobody filled in. See exactly who
                would be written to, and about what, before it goes.
              </p>

              <Button
                loading={digestLoading}
                disabled={digestLoading}
                onClick={() => loadDigestPreview()}
              >
                Preview tomorrow's emails
              </Button>

              {digestError && (
                <Message error content={digestError.message} style={{ marginTop: '1em' }} />
              )}

              {preview && (
                <div style={{ marginTop: '1.5em' }}>
                  <p style={{ color: '#767676', fontSize: '0.9em' }}>
                    Sending <strong>{preview.sendsAt}</strong> ({preview.timezone}), covering{' '}
                    <strong>{preview.period}</strong> — registers are chased for{' '}
                    {preview.lookbackDays} days and then stop appearing.
                  </p>

                  {/* It is a forecast, and saying so is the difference between a useful screen
                      and one people stop trusting: the window includes today, so every register
                      filled between now and 9am drops a line. */}
                  <Message info size="tiny">
                    This is how it looks now. Any register filled between now and the send will
                    drop off it.
                  </Message>

                  {preview.recipients.length === 0 ? (
                    <Message
                      positive
                      header="Nobody would be emailed"
                      content="Every register in the window has been filled in."
                    />
                  ) : (
                    preview.recipients.map((r: any) => (
                      <Segment key={r.managerUserId} secondary style={{ marginBottom: '0.8em' }}>
                        <div style={{ fontWeight: 600 }}>
                          {r.email}
                          {r.managerName && (
                            <span style={{ fontWeight: 400, color: '#767676' }}> — {r.managerName}</span>
                          )}
                        </div>
                        <ul style={{ margin: '0.5em 0 0 0', paddingLeft: '1.2em' }}>
                          {r.classes.map((c: any) => (
                            <li key={`${c.courseId}-${c.day}-${c.startedAt}`} style={{ fontSize: '0.92em' }}>
                              <strong>{c.day} {c.startedAt}</strong> {c.courseName}{' '}
                              <span style={{ color: '#767676' }}>
                                ({c.expected} expected
                                {c.teacherNames.length > 0 && ` — ${c.teacherNames.join(', ')}`})
                              </span>
                            </li>
                          ))}
                        </ul>
                      </Segment>
                    ))
                  )}

                  {/* A class with no manager is reported to nobody. Without this line, twelve
                      unassigned classes and twelve filled registers look identical here. */}
                  {preview.unmanagedClasses > 0 && (
                    <Message warning size="tiny">
                      {preview.unmanagedClasses} class{preview.unmanagedClasses === 1 ? '' : 'es'} in
                      this window {preview.unmanagedClasses === 1 ? 'has' : 'have'} no manager set,
                      so nobody is told about {preview.unmanagedClasses === 1 ? 'it' : 'them'}.
                    </Message>
                  )}
                </div>
              )}
            </Segment>
            <Divider />
          </>
        )}

        {/* Shown to anyone who can reach a stream, not only administrators — content reviewers
            manage streams, and the page gates itself on the same answer. */}
        {sections.has('STREAMS') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Streams</Header>
              <p style={{ color: '#767676' }}>
                Where posts are published, and who can read them.
              </p>
              <Button as="a" href={PATHS.ADMIN_STREAMS}>Manage streams</Button>
            </Segment>
            <Divider />
          </>
        )}

        {/* `publishAt` is a bare instant and the scheduler picks it in the browser's zone, so
            "Sunday 9am" has always meant 9am wherever the person scheduling was standing. That
            was harmless while people only scheduled for their own community; it stopped being
            harmless once a post could be authored into another organisation's stream.

            Not gated on classes: every organisation has an audience somewhere, including a
            supporter organisation publishing to its own leadership stream. */}
        {sections.has('TIME_ZONE') && (
          <>
            <Segment>
              <Header as="h3" style={{ marginBottom: '0.2em' }}>Time zone</Header>
              <p style={{ color: '#767676' }}>
                Where this organisation's audience is. Scheduling a post then shows their local
                time alongside yours, so "Sunday 9am" means 9am to the people reading it.
              </p>
              <Form error={!!timezoneError}>
                <Form.Field>
                  <Dropdown
                    search
                    selection
                    clearable
                    placeholder="Not set — schedules show your own time only"
                    options={timezoneOptions}
                    value={timezoneValue}
                    onChange={(_, { value }) => setTimezoneDraft(String(value ?? ''))}
                  />
                </Form.Field>
                <Message error content={timezoneError?.message} />
                <Button
                  primary
                  loading={savingTimezone}
                  disabled={savingTimezone || timezoneValue === (storedTimezone ?? '')}
                  onClick={() => setTimezone({ variables: { timezone: timezoneValue || null } })}
                >
                  Save time zone
                </Button>
              </Form>
            </Segment>
            <Divider />
          </>
        )}

        {/* Gated on there being a block tag to *choose*, not on the organisation running
            classes. Both dropdowns take their options from `blockTags`, which is empty when the
            organisation has no tag group named "block" — so this used to render two empty
            selects above a Save button that could only write null. The test is therefore not
            "is this relevant" but "can this control function at all". */}
        {sections.has('BLOCK_SETTINGS') && (
          <>
        <Header as="h2">Block Settings</Header>
        <Form success={saved} error={!!error}>
          <Form.Field>
            <label>Current block</label>
            <Dropdown
              selection
              loading={tagsLoading || blocksLoading}
              options={tagOptions}
              value={currentId ?? undefined}
              placeholder="Select current block..."
              onChange={(_, { value }) => {
                const v = value as number;
                setCurrentId(v);
                if (v === nextId) setNextId(null);
                setSaved(false);
              }}
            />
          </Form.Field>
          <Form.Field>
            <label>Next block</label>
            <Dropdown
              selection
              loading={tagsLoading || blocksLoading}
              options={nextTagOptions}
              value={nextId != null ? nextId : NONE}
              onChange={(_, { value }) => {
                setNextId(value === NONE ? null : (value as number));
                setSaved(false);
              }}
            />
          </Form.Field>
          {saved && <Message success content="Settings saved." />}
          {error && <Message error content={error.message} />}
          <Button primary loading={saving} onClick={handleSave}>
            Save
          </Button>
        </Form>

        <Divider section />
          </>
        )}

        {/* Stays visible in every organisation, including supporter ones. It reaches this
            organisation's own subscribed members, which is a real audience anywhere — and when
            there are none, `recipientSummary()` already says so honestly rather than pretending.

            The old subheader claimed it sent "to app users in supporting organisations", which
            was wrong in both directions: `sendAnnouncement` resolves its topic against the
            working organisation and `announcementRecipientCount` filters subscribers to that
            organisation's live memberships. It reaches here, and nowhere else. */}
        {sections.has('PUSH_NOTIFICATIONS') && (
          <>
        <Header as="h2">
          Push Notifications
          <Header.Subheader>
            Send a push notification to {organisationName ?? 'this organisation'}'s own app users
            who are subscribed to this topic, on both iOS and Android. It does not reach any other
            organisation. Only people with the app installed and notifications enabled receive it.
          </Header.Subheader>
        </Header>
        <Form success={sent} error={!!sendError}>
          <Form.Field>
            <label>Type</label>
            <Dropdown
              selection
              options={KIND_OPTIONS}
              value={kind}
              onChange={(_, { value }) => { setKind(value as 'APPLICATION' | 'PRODUCT'); setSent(false); }}
            />
            <div style={{ marginTop: '0.5em', color: 'rgba(0,0,0,0.6)' }}>
              {recipientSummary()}
            </div>
          </Form.Field>
          <Form.Field>
            <label>Title</label>
            <Input
              value={title}
              placeholder="Announcement title"
              onChange={(e) => { setTitle(e.target.value); setSent(false); }}
            />
          </Form.Field>
          <Form.Field>
            <label>Body</label>
            <TextArea
              value={body}
              placeholder="Announcement body"
              rows={4}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setBody(e.target.value); setSent(false); }}
            />
          </Form.Field>
          {sent && <Message success content="Announcement sent!" />}
          {sendError && <Message error content={sendError.message} />}
          <Button
            primary
            loading={sending}
            disabled={!title.trim() || !body.trim()}
            onClick={handleSend}
          >
            Send
          </Button>
        </Form>
          </>
        )}
      </Container>
    </ResponsiveContainer>
  );
};
